'use client';

import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { Button, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useState, FC } from 'react';
import { toast } from 'react-toastify';

import { PORTALS } from '@/.constants/portals';
import DoubleArrowIcon from '@/public/icons/double_arrow.svg';
import EraseIcon from '@/public/icons/erase.svg';
import BrushIcon from '@/public/icons/mask_brush.svg';
import DeleteIcon from '@/public/icons/mask_cross.svg';
import UndoIcon from '@/public/icons/undo.svg';
import { PromptArea } from '@/src/components/PromptArea';
import { ETool, useAutoMaskContext } from '@/src/providers';
import { cn } from '@/src/utils/cn';

import { AutoMask } from './auto-mask';
import { BrushSize } from './BrushSize';

const styles = {
  button: '!w-12 h-12 !p-0 !bg-fill-toast',
  buttonNext: '!w-[72px] !bg-fill-button !border-0',
  buttonNextDisable: '!bg-fill-toast',
  icon: 'w-6 h-6',
};

interface MaskBlockProps {
  imageId: string;
  image: string;
  onClickGenerate: (prompt: string) => Promise<void>;
  loading: boolean;
}

export const MaskBlock: FC<MaskBlockProps> = ({
  imageId,
  image,
  onClickGenerate,
  loading,
}) => {
  const t = useTranslations('InpaintPage');

  const { openPortal } = usePortalContext();
  const {
    currentTool,
    setTool,
    setUndoDraw,
    setCleanDraw,
    setInpaintFile,
    inpaintFile,
    hasMask,
  } = useAutoMaskContext();

  const [prompt, setPrompt] = useState<string>('');
  const [buttonDisabled, setButtonDisabled] = useState(false);

  useEffect(() => {
    setInpaintFile((prev) => ({
      ...prev,
      id: imageId,
      fileUrl: image,
    }));
  }, [image, imageId, setInpaintFile]);

  const handleClickGenerate = async () => {
    try {
      if (!hasMask) {
        toast.error(t('emptyMask'));
        return;
      }

      setButtonDisabled(true);

      await onClickGenerate(prompt);

      setTimeout(() => {
        setButtonDisabled(false);
      }, 5000);
    } catch (err) {
      const error = err as Error;
      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      } else {
        console.error(error);
      }

      setTimeout(() => {
        setButtonDisabled(false);
      }, 5000);
    }
  };

  const disabled = buttonDisabled || loading;

  return (
    <div className="pt-4 mb-16">
      <div className="flex gap-2 mb-1">
        <BrushIcon className="w-5 h-5" />
        <div className="font-bold text-base">{t('title')}</div>
      </div>
      <div className="font-medium text-sm text-label-secondary">
        {t('text')}
      </div>
      <div className="w-full h-[335px] rounded-lg relative mt-3 mb-4 overflow-hidden">
        <Image
          src="/images/inpaint-bg.webp"
          width={826}
          height={826}
          quality={95}
          priority
          alt=""
          className="pointer-events-none block absolute z-0 size-full translate-z-0 object-cover select-none"
        />
        {inpaintFile.fileUrl && (
          <Image
            src={inpaintFile.fileUrl}
            width={826}
            height={826}
            quality={100}
            priority
            alt=""
            className="pointer-events-none block size-full translate-z-0 absolute z-1 object-contain select-none"
          />
        )}
        <AutoMask id={imageId} image={image} />
      </div>

      <PromptArea
        changePrompt={setPrompt}
        prompt={prompt}
        maxLength={100}
        placeholder={t('textarea')}
        className="!bg-fill-quinary !text-sm !font-normal !min-h-16 !h-16 !border-yellow"
      />
      <BrushSize />
      <div className="flex w-full gap-2 justify-between bg-fill-quinary p-3 rounded-b-xl">
        <Button
          key="button-undo"
          onClick={() => setUndoDraw(true)}
          className={styles.button}
        >
          <UndoIcon className={styles.icon} />
        </Button>

        <Button
          key="button-brush"
          onClick={() => setTool(ETool.BRUSH)}
          className={cn(
            styles.button,
            currentTool === ETool.BRUSH && '!bg-fill-rose',
          )}
        >
          <BrushIcon className={styles.icon} />
        </Button>

        <Button
          key="button-rubber"
          onClick={() => setTool(ETool.RUBBER)}
          className={cn(
            styles.button,
            currentTool === ETool.RUBBER && '!bg-fill-rose',
          )}
        >
          <EraseIcon className={styles.icon} />
        </Button>

        <Button
          key="button-delete"
          onClick={() => setCleanDraw(true)}
          className={styles.button}
        >
          <DeleteIcon className={styles.icon} />
        </Button>

        <Button
          key="button-save-mask"
          disabled={!prompt || disabled}
          onClick={handleClickGenerate}
          className={cn(
            styles.button,
            styles.buttonNext,
            (!prompt || disabled) && styles.buttonNextDisable,
          )}
        >
          <DoubleArrowIcon />
        </Button>
      </div>
    </div>
  );
};
